/**
 * Service Store
 * متجر إدارة الخدمات
 */

import { makeAutoObservable } from 'mobx';
import api from '../services/api';

class ServiceStore {
  services = [];
  loading = false;
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchServices() {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await api.api.get('/admin/services');
      if (response.data.status === 'success') {
        this.services = response.data.data.data || [];
      } else {
        this.error = response.data.message || 'فشل في تحميل الخدمات';
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      this.error = 'فشل في تحميل الخدمات';
    } finally {
      this.loading = false;
    }
  }

  async createService(serviceData) {
    this.loading = true;
    this.error = null;
    
    try {
      const formData = new FormData();
      
      // Basic service data
      formData.append('service_provider_id', serviceData.service_provider_id);
      formData.append('category_id', serviceData.category_id);
      formData.append('city_id', serviceData.city_id);
      formData.append('name', serviceData.name);
      formData.append('description', serviceData.description);
      formData.append('website', serviceData.website || '');
      formData.append('longitude', serviceData.longitude || '');
      formData.append('latitude', serviceData.latitude || '');
      formData.append('is_approved', serviceData.is_approved ? '1' : '0');
      
      // Working days
      if (serviceData.working_days && serviceData.working_days.length > 0) {
        for (const [index, day] of serviceData.working_days.entries()) {
          formData.append(`working_days[${index}][day]`, day.day);
          formData.append(`working_days[${index}][start]`, day.start);
          formData.append(`working_days[${index}][end]`, day.end);
        }
      }
      
      // Social links
      if (serviceData.social_links && serviceData.social_links.length > 0) {
        for (const [index, link] of serviceData.social_links.entries()) {
          formData.append(`social_links[${index}][platform]`, link.platform);
          formData.append(`social_links[${index}][url]`, link.url);
        }
      }
      
      // Images
      if (serviceData.images && serviceData.images.length > 0) {
        for (const [index, image] of serviceData.images.entries()) {
          formData.append(`images[${index}]`, image);
        }
      }

      const response = await api.api.post('/admin/services', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.status === 'success') {
        await this.fetchServices(); // Refresh the list
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'فشل في إنشاء الخدمة');
      }
    } catch (error) {
      console.error('Error creating service:', error);
      this.error = error.message || 'فشل في إنشاء الخدمة';
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async updateService(serviceId, serviceData) {
    this.loading = true;
    this.error = null;
    
    try {
      const formData = new FormData();
      
      // Basic service data
      formData.append('service_provider_id', serviceData.service_provider_id);
      formData.append('category_id', serviceData.category_id);
      formData.append('city_id', serviceData.city_id);
      formData.append('name', serviceData.name);
      formData.append('description', serviceData.description);
      formData.append('website', serviceData.website || '');
      formData.append('longitude', serviceData.longitude || '');
      formData.append('latitude', serviceData.latitude || '');
      formData.append('is_approved', serviceData.is_approved ? '1' : '0');
      
      // Working days
      if (serviceData.working_days && serviceData.working_days.length > 0) {
        for (const [index, day] of serviceData.working_days.entries()) {
          formData.append(`working_days[${index}][day]`, day.day);
          formData.append(`working_days[${index}][start]`, day.start);
          formData.append(`working_days[${index}][end]`, day.end);
        }
      }
      
      // Social links
      if (serviceData.social_links && serviceData.social_links.length > 0) {
        for (const [index, link] of serviceData.social_links.entries()) {
          formData.append(`social_links[${index}][platform]`, link.platform);
          formData.append(`social_links[${index}][url]`, link.url);
        }
      }
      
      // Images
      if (serviceData.images && serviceData.images.length > 0) {
        for (const [index, image] of serviceData.images.entries()) {
          formData.append(`images[${index}]`, image);
        }
      }

      const response = await api.api.put(`/admin/services/${serviceId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.status === 'success') {
        await this.fetchServices(); // Refresh the list
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'فشل في تحديث الخدمة');
      }
    } catch (error) {
      console.error('Error updating service:', error);
      this.error = error.message || 'فشل في تحديث الخدمة';
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async deleteService(serviceId) {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await api.api.delete(`/admin/services/${serviceId}`);
      
      if (response.data.status === 'success') {
        await this.fetchServices(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.message || 'فشل في حذف الخدمة');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      this.error = error.message || 'فشل في حذف الخدمة';
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async approveService(serviceId) {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await api.api.put(`/admin/services/${serviceId}/approve`);
      
      if (response.data.status === 'success') {
        await this.fetchServices(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.message || 'فشل في الموافقة على الخدمة');
      }
    } catch (error) {
      console.error('Error approving service:', error);
      this.error = error.message || 'فشل في الموافقة على الخدمة';
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async rejectService(serviceId) {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await api.api.put(`/admin/services/${serviceId}/reject`);
      
      if (response.data.status === 'success') {
        await this.fetchServices(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.message || 'فشل في رفض الخدمة');
      }
    } catch (error) {
      console.error('Error rejecting service:', error);
      this.error = error.message || 'فشل في رفض الخدمة';
      throw error;
    } finally {
      this.loading = false;
    }
  }

  getServiceById(id) {
    return this.services.find(service => service.id === id);
  }

  getServicesByCategory(categoryId) {
    return this.services.filter(service => service.category_id === categoryId);
  }

  getServicesByProvider(providerId) {
    return this.services.filter(service => service.service_provider_id === providerId);
  }

  getApprovedServices() {
    return this.services.filter(service => service.is_approved);
  }

  getPendingServices() {
    return this.services.filter(service => !service.is_approved);
  }
}

export const serviceStore = new ServiceStore();
